import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import Layout from 'components/common/layout/Layout';
import SearchFilter from 'components/common/forms/SearchFilter';
import StudentInfo from 'components/pages/professor/student/StudentInfo';
import ChartSwiper from 'components/common/lists/ChartSwiper';
import StudentChartList from 'components/pages/professor/student/StudentChartList';
import Footer from 'components/common/layout/Footer';
import styles from 'styles/pages/professor/StudentDetail.module.scss';
import { ReactComponent as CircleCheckIcon } from 'assets/icons/circle_check.svg';
import { ReactComponent as CircleCheckOnIcon } from 'assets/icons/circle_check_on.svg';
import { ROUTE_PATH } from 'constants/routePath';
import { DEFAULT_PAGE_SIZE } from 'constants/page';
import { ChartInfo } from 'constants/chart/common/chartInfo';
import { TChartGradingStatus } from 'types/chart';
import { SortDirection } from 'types/apis/common';
import {
  TStudentChartListParams,
  TStudentChartTableInfo,
  StudentChartListSortBy,
  StudentChartListSortByFromLabel,
} from 'types/apis/professor';
import { getStudentDetailInfo, getStudentChartList } from 'apis/professor';
import useModal from 'hooks/useModal';

const StudentDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { id } = useParams();

  const { openModal } = useModal();

  const [from, setFrom] = useState(ROUTE_PATH.professor.STUDENT);
  const [page, setPage] = useState(Number(query.get('page')) || 1);
  const [params, setParams] = useState({
    page,
    size: DEFAULT_PAGE_SIZE,
    courseInfoId: Number(query.get('courseInfoId')) || undefined,
    chartRound: Number(query.get('chartRound')) || undefined,
    sortBy: query.get('sortBy') || undefined,
    isAsc:
      query.get('sort') === SortDirection.Asc
        ? true
        : query.get('sort') === SortDirection.Desc
        ? false
        : undefined,
  });
  const [sortBy, setSortBy] = useState(query.get('sortBy') || '');
  const [sortDirection, setSortDirection] = useState(query.get('sort') || '');
  const [recentRound, setRecentRound] = useState<number | undefined>(undefined);
  const [statusData, setStatusData] = useState<
    TChartGradingStatus[] | undefined
  >();
  const [courseId, setCourseId] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (location.state?.from) {
      setFrom(location.state.from);
    }
  }, []);

  useEffect(() => {
    if (sortBy !== query.get('sortBy')) setSortBy('');
    if (sortDirection !== query.get('sort')) setSortDirection('');
    setParams({
      ...params,
      page,
      courseInfoId: Number(query.get('courseInfoId')) || undefined,
      chartRound: Number(query.get('chartRound')) || undefined,
    });
  }, [location.search, page]);

  useEffect(() => {
    window.addEventListener('popstate', goToList);

    return () => {
      window.removeEventListener('popstate', goToList);
    };
  }, [courseId]);

  const onError = () =>
    openModal({
      title: '학생 조회 실패',
      text: '다시 시도해 주세요.',
      onSubmit: () => navigate(from),
    });

  const { data: getStudentDetailInfoData } = useQuery(
    ['getStudentDetailInfo', id, params.courseInfoId],
    () => getStudentDetailInfo(Number(id), Number(params.courseInfoId)),
    {
      enabled: !!id && !!params.courseInfoId,
      onSuccess: ({ data }) => {
        setRecentRound(data?.chartRound);
        data?.chartStatus &&
          setStatusData(
            ChartInfo.map((chart) => {
              const type =
                chart.type.toString() as keyof typeof data.chartStatus;
              return {
                chartName: chart.name,
                status:
                  data.chartStatus[type] === '작성완료'
                    ? 2
                    : data.chartStatus[type] === '작성중'
                    ? 1
                    : data.chartStatus[type] === '미작성'
                    ? 0
                    : null,
              };
            })
          );
        setCourseId(data?.courseId);
      },
      onError,
    }
  );

  const { data: getStudentChartListData, isFetching } = useQuery(
    ['getStudentChartList', id, params],
    () => getStudentChartList(Number(id), params as TStudentChartListParams),
    { enabled: !!id && !!params.courseInfoId && !!params.chartRound, onError }
  );

  const tableData: TStudentChartTableInfo[] =
    getStudentChartListData?.data?.chartDetailRespList || [];
  const tablePaging = getStudentChartListData?.data?.paging;

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  const onClickSort = (label: string) => {
    const sortAsc =
      sortDirection === ''
        ? SortDirection.Asc
        : sortDirection === SortDirection.Asc
        ? SortDirection.Desc
        : '';
    const sortBy = {
      [StudentChartListSortByFromLabel.LastEvaluatedAt]:
        StudentChartListSortBy.LastEvaluatedAt,
      [StudentChartListSortByFromLabel.Score]: StudentChartListSortBy.Score,
    }[label as StudentChartListSortByFromLabel];

    setSortDirection(sortAsc);
    setSortBy(sortBy);

    if (page !== 1) {
      setPage(1);
    }
    if (!sortAsc) {
      query.delete('sort');
      query.delete('sortBy');
    } else {
      query.set('sort', sortAsc);
      query.set('sortBy', sortBy);
    }
    navigate({
      search: query.toString(),
    });

    setParams({
      ...params,
      page: 1,
      sortBy: (query.get('sortBy') as StudentChartListSortBy) || undefined,
      isAsc:
        sortAsc === SortDirection.Asc
          ? true
          : sortAsc === SortDirection.Desc
          ? false
          : undefined,
    });
  };

  const goToList = () =>
    navigate(
      `${ROUTE_PATH.professor.STUDENT}${
        courseId
          ? `?courseId=${courseId}&courseInfoId=${params.courseInfoId}&registrationStatus=재학`
          : ''
      }`
    );

  return (
    <div className="min_h100vh flex flex_column">
      <div className="grow_1">
        <Layout bgColor={true} hideFooter={true}>
          <div className="flex justify_end mt32 mb14">
            <SearchFilter
              isInnerItem={true}
              dropdown={[
                {
                  id: 'studentCourseByProfessor',
                  placeholder: '강의명',
                  width: '200px',
                },
                {
                  id: 'chartRound',
                  placeholder: '(회차) 회차명',
                  width: '200px',
                },
              ]}
              onChange={() => setPage(1)}
            />
            <button
              type="button"
              className={styles.list_btn}
              onClick={goToList}
            >
              목록
            </button>
          </div>

          <div className={styles.top_wrapper}>
            {/* 학생 정보 */}
            <StudentInfo data={getStudentDetailInfoData?.data} />

            {/* 차트 작성 현황 */}
            <div className={styles.chart_card_wrapper}>
              <div className={styles.chart_title}>
                <h2 className="fw700">
                  최근 차트 작성 현황 :{' '}
                  {recentRound ? `${recentRound}회차` : '-'}
                </h2>
                <div className="flex items_center justify_between">
                  <CircleCheckOnIcon width={20} height={20} />
                  <span className="fs12 ml7 mr16 fw700">: 작성완료</span>
                  <CircleCheckIcon width={20} height={20} />
                  <span className="fs12 ml7 fw700">: 작성중</span>
                </div>
              </div>
              <ChartSwiper
                data={statusData}
                height={74}
                padding="8px 11px"
                marginBottom="32px"
                fontSize="12px"
                iconWidth={32}
                iconHeight={32}
              />
            </div>
          </div>
        </Layout>

        {/* 등록된 차트 */}
        <StudentChartList
          courseInfoId={params.courseInfoId}
          chartRound={params.chartRound}
          studentUserId={Number(id)}
          tableData={tableData}
          tablePaging={tablePaging}
          page={page}
          setPage={setPage}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onClickSort={onClickSort}
          isLoading={isLoading}
        />
      </div>
      <Footer />
    </div>
  );
};

export default StudentDetail;
